import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import promocode_service from "../../services/promocode_service";
import events_service from "../../services/events_service";

interface Promocode {
  id: string;
  code: string;
  discount_percent: number;
  is_active: boolean;
  all_events: boolean;
  created_at: string;
  event_ids: string[];
}

interface EventOption {
  event_id: string;
  eventName_en: string;
  eventName_am: string;
  dateTime: string;
}

interface PromocodeForm {
  id?: string;
  code: string;
  discount_percent: string;
  is_active: boolean;
  all_events: boolean;
  event_ids: string[];
}

const emptyForm: PromocodeForm = {
  code: "",
  discount_percent: "",
  is_active: true,
  all_events: false,
  event_ids: [],
};

const PromocodesPage: React.FC = () => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  const [loading, setLoading] = useState(true);
  const [promocodes, setPromocodes] = useState<Promocode[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<PromocodeForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadPromocodes = () =>
    promocode_service.getPromocodes().then((res) => {
      setPromocodes(res.data.status.promocodes || []);
    });

  useEffect(() => {
    Promise.all([
      loadPromocodes(),
      events_service.getEventsList().then((res) => {
        setEvents(res.data.status.events || []);
      }),
    ])
      .catch(() => toast.error("Failed to load promo codes."))
      .finally(() => setLoading(false));
  }, []);

  const eventsById = useMemo(() => {
    const map: { [id: string]: EventOption } = {};
    events.forEach((event) => (map[event.event_id] = event));
    return map;
  }, [events]);

  // Only upcoming events can be picked; past events already on a code still show as its chips
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return events
      .filter((event) => event.dateTime && new Date(event.dateTime) >= now)
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
  }, [events]);

  const eventLabel = (event: EventOption) => {
    const name = (language === "am" ? event.eventName_am : event.eventName_en) || event.eventName_en || event.eventName_am;
    const date = event.dateTime ? event.dateTime.substring(0, 16) : "";
    return `${name} (${date ? date + ", " : ""}#${event.event_id})`;
  };

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (promocode: Promocode) => {
    setForm({
      id: promocode.id,
      code: promocode.code,
      discount_percent: String(promocode.discount_percent),
      is_active: promocode.is_active,
      all_events: promocode.all_events,
      event_ids: promocode.event_ids,
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    const code = form.code.trim().toUpperCase();
    const percent = Number(form.discount_percent);
    if (!/^[A-Z0-9_-]{3,50}$/.test(code)) {
      toast.error("Code must be 3-50 characters: letters, digits, - or _");
      return;
    }
    if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
      toast.error("Discount must be a whole number between 1 and 100.");
      return;
    }
    if (!form.all_events && form.event_ids.length === 0) {
      toast.error("Select at least one event.");
      return;
    }

    setSaving(true);
    promocode_service
      .savePromocode({
        ...(form.id ? { id: form.id } : {}),
        code,
        discount_percent: percent,
        is_active: form.is_active ? 1 : 0,
        all_events: form.all_events ? 1 : 0,
        event_ids: form.all_events ? "" : form.event_ids.join(","),
      })
      .then((res) => {
        const status = res.data.status;
        if (status.status === "success") {
          toast.success("Promo code saved.");
          setDialogOpen(false);
          return loadPromocodes();
        }
        toast.error(status.message || "Failed to save promo code.");
      })
      .catch(() => toast.error("Failed to save promo code."))
      .finally(() => setSaving(false));
  };

  const handleDelete = (promocode: Promocode) => {
    Swal.fire({
      title: `Delete promo code ${promocode.code}?`,
      text: "Customers will no longer be able to use it. Orders already paid with it are not changed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#E53E3E",
    }).then((result) => {
      if (!result.isConfirmed) return;
      promocode_service
        .deletePromocode({ id: promocode.id })
        .then(() => {
          setPromocodes((prev) => prev.filter((p) => p.id !== promocode.id));
          toast.success("Promo code deleted.");
        })
        .catch(() => toast.error("Failed to delete promo code."));
    });
  };

  const handleToggleActive = (promocode: Promocode) => {
    promocode_service
      .savePromocode({
        id: promocode.id,
        code: promocode.code,
        discount_percent: promocode.discount_percent,
        is_active: promocode.is_active ? 0 : 1,
        all_events: promocode.all_events ? 1 : 0,
        event_ids: promocode.event_ids.join(","),
      })
      .then((res) => {
        if (res.data.status.status === "success") {
          setPromocodes((prev) =>
            prev.map((p) => (p.id === promocode.id ? { ...p, is_active: !p.is_active } : p))
          );
        } else {
          toast.error(res.data.status.message || "Failed to update promo code.");
        }
      })
      .catch(() => toast.error("Failed to update promo code."));
  };

  const selectedEvents = form.event_ids.map(
    (id) => eventsById[id] || { event_id: id, eventName_en: `Event #${id}`, eventName_am: "", dateTime: "" }
  );

  return (
    <Box sx={{ width: "100%", height: "88vh", pb: 2, overflowY: "auto" }}>
      <ToastContainer />
      <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ width: "98%", backgroundColor: "#fff", padding: "20px", mt: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="body1" style={{ fontWeight: 800 }}>
            Promo Codes
          </Typography>
          <Button
            variant="contained"
            size="small"
            sx={{ backgroundColor: "#4D67EB", fontSize: "12px", color: "#fff", "&:hover": { backgroundColor: "#3755a3" } }}
            onClick={openCreate}
          >
            <AddCircleOutlineIcon sx={{ mr: 1 }} />
            Add New Promo Code
          </Button>
        </Box>

        {!loading && promocodes.length === 0 ? (
          <Typography sx={{ color: "text.secondary", p: 2 }}>
            No promo codes yet. Use "Add New Promo Code" to create one.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CODE</TableCell>
                <TableCell>DISCOUNT</TableCell>
                <TableCell>EVENTS</TableCell>
                <TableCell>ACTIVE</TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {promocodes.map((promocode) => (
                <TableRow key={promocode.id}>
                  <TableCell sx={{ fontWeight: 700 }}>{promocode.code}</TableCell>
                  <TableCell>{promocode.discount_percent}%</TableCell>
                  <TableCell sx={{ maxWidth: 480 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {promocode.all_events && (
                        <Chip size="small" color="primary" label="All events (including new ones)" />
                      )}
                      {!promocode.all_events && promocode.event_ids.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={eventsById[id] ? eventLabel(eventsById[id]) : `Event #${id} (deleted)`}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Switch checked={promocode.is_active} onChange={() => handleToggleActive(promocode)} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(promocode)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDelete(promocode)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? "Edit Promo Code" : "Add New Promo Code"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField
            label="Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, "") })}
            helperText="Customers type this on the ticket purchase page. Letters, digits, - or _"
            inputProps={{ maxLength: 50 }}
            fullWidth
          />
          <TextField
            label="Discount"
            type="number"
            value={form.discount_percent}
            onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 1, max: 100, step: 1 }}
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={form.all_events} onChange={(e) => setForm({ ...form, all_events: e.target.checked })} />}
            label="Valid for all events"
          />
          {form.all_events ? (
            <Typography sx={{ fontSize: "13px", color: "text.secondary", mt: -1 }}>
              The code works for every event, including events created later - no need to select them.
            </Typography>
          ) : (
          <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            options={upcomingEvents}
            value={selectedEvents}
            getOptionLabel={eventLabel}
            isOptionEqualToValue={(option, value) => option.event_id === value.event_id}
            onChange={(_, value) => setForm({ ...form, event_ids: value.map((event) => event.event_id) })}
            renderOption={(props, option) => (
              <li {...props} key={option.event_id}>
                {eventLabel(option)}
              </li>
            )}
            noOptionsText="No more upcoming events"
            renderInput={(params) => <TextField {...params} label="Valid for upcoming events" placeholder="Search events" />}
          />
          )}
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: "#4D67EB", "&:hover": { backgroundColor: "#3755a3" } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromocodesPage;
